import React from "react";
import { Box, Typography } from "@mui/material";
import { containerLabel, formatMoney, formatWhole } from "./groupChargesByContainer";

export const RATE_COLORS = {
  price: "#E8890C",
  divider: "#F1E6CF",
  text: "#111827",
  secondary: "#6B7280",
  muted: "#9CA3AF",
  error: "#C0392B",
  errorBg: "#FDECEA",
};

// "1 × 45HC (1,200 kg)"
export const unavailableLabel = (c) =>
  c.weightKg > 0
    ? `${containerLabel(c)} (${formatWhole(c.weightKg)} kg)`
    : containerLabel(c);

export const unavailableList = (list) => {
  let text = "";
  for (let i = 0; i < list.length; i++) {
    text += (i > 0 ? ", " : "") + unavailableLabel(list[i]);
  }
  return text;
};

// Container rates (USD) cell of an ocean rate row: one line per priced
// container ("2 × 20GP  26,000 kg … 2 × 5,882 = 11,764.08") and, when any
// searched container has no rate, a red coverage footer.
// `breakdown` is the result of groupChargesByContainer. Needs ~300px; the
// row's grid track gives it that.
const ContainerRatesCell = ({ breakdown }) => {
  const { priced, unavailable, requestedCount, pricedRequestedCount } = breakdown;

  if (priced.length === 0 && unavailable.length === 0) {
    return <Typography sx={{ fontSize: 11, color: RATE_COLORS.muted }}>—</Typography>;
  }

  return (
    <Box sx={{ minWidth: 0, width: "100%" }}>
      {priced.map((g, i) => (
        <Box
          key={g.key}
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 1,
            py: 0.625,
            borderTop: i > 0 ? `1px solid ${RATE_COLORS.divider}` : "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
            <Typography
              component="span"
              sx={{ fontSize: 12.5, fontWeight: 600, color: RATE_COLORS.text, whiteSpace: "nowrap" }}
            >
              {containerLabel(g)}
            </Typography>
            {g.weightKg > 0 && (
              <Typography component="span" noWrap sx={{ fontSize: 10.5, color: RATE_COLORS.muted }}>
                {formatWhole(g.weightKg)} kg
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.625, flexShrink: 0 }}>
            {g.qty !== null && g.unitRate !== null && (
              <Typography
                component="span"
                sx={{ fontSize: 10.5, color: RATE_COLORS.secondary, whiteSpace: "nowrap" }}
              >
                {g.qty} × {formatWhole(g.unitRate)} =
              </Typography>
            )}
            <Typography
              component="span"
              sx={{ fontSize: 13, fontWeight: 600, color: RATE_COLORS.price, whiteSpace: "nowrap" }}
            >
              {formatMoney(g.total)}
            </Typography>
          </Box>
        </Box>
      ))}

      {unavailable.length > 0 && (
        <Box
          role="note"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mt: priced.length > 0 ? 0.75 : 0,
            px: 1.25,
            py: 0.75,
            borderRadius: "10px",
            bgcolor: RATE_COLORS.errorBg,
            color: RATE_COLORS.error,
          }}
        >
          <Typography sx={{ fontSize: 10.5, fontWeight: 500, color: "inherit", minWidth: 0 }}>
            ⚠ {unavailableList(unavailable)} – rate not available
          </Typography>
          <Typography
            sx={{ fontSize: 10.5, fontWeight: 600, color: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {pricedRequestedCount} of {requestedCount} priced
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ContainerRatesCell;
